/**
 * Client-side OGC Process caller for ODIN data.
 * Replaces the BFF route handlers from ArturoSB's Next.js API routes.
 * Calls /oapi/processes/* directly from the browser.
 */

import { OgcFeatureCollection, SpiderResponse, SpiderProfileResponse } from '@/types/odin';

const OGC_BASE = '/oapi';

type ProcessResult<T = unknown> = { data: T } | { error: string };

/**
 * Execute an OGC Process with 1 retry (handles stale asyncpg connections).
 */
async function executeProcess<T = unknown>(
  processId: string,
  inputs: Record<string, string>,
): Promise<ProcessResult<T>> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await fetch(`${OGC_BASE}/processes/${processId}/execution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inputs }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => 'Unknown error');
        // Retry on stale connection errors (TCPTransport closed)
        if (attempt === 0 && text.includes('handler is closed')) continue;
        return { error: `${processId} failed (${res.status}): ${text}` };
      }
      return { data: await res.json() };
    } catch (err) {
      if (attempt === 0) continue;
      return { error: `${processId} network error: ${err instanceof Error ? err.message : 'Unknown'}` };
    }
  }
  return { error: `${processId} failed after retry` };
}

/**
 * Combined ODIN data response shape (matches the old BFF output).
 */
export interface OdinDataResponse {
  postcode: string | null;
  gemeente: string | null;
  province: string | null;
  lat: number | null;
  lng: number | null;
  odin: unknown | null;
  profile: unknown | null;
  pc4_properties: Record<string, unknown> | null;
  process_errors?: Record<string, string>;
}

/**
 * Fetch ODIN data for a location. Replaces BFF `/api/odin`.
 *
 * Accepts one of:
 *   { lat, lng }        - point click (reverse-geocode to PC4)
 *   { postcode }        - PC4 boundary click
 *   { municipality }    - gemeente boundary click
 *   { province }        - provincie boundary click
 *
 * Optional filters: mode, purpose, distance
 */
export async function fetchOdinData(params: {
  lat?: number;
  lng?: number;
  postcode?: string;
  municipality?: string;
  province?: string;
  mode?: string;
  purpose?: string;
  distance?: string;
  year_min?: number;
  year_max?: number;
}): Promise<{ data: OdinDataResponse } | { error: string }> {
  try {
    let odinInputs: Record<string, string>;
    let profileInputs: Record<string, string>;
    let resolvedPostcode: string | null = null;
    let resolvedGemeente: string | null = null;
    let resolvedProvince: string | null = null;
    let pc4Props: Record<string, unknown> | null = null;

    if (params.postcode) {
      // Mode: direct PC4 lookup
      odinInputs = { postcode: params.postcode, include_cross_tabs: 'true' };
      profileInputs = { postcode: params.postcode };
      resolvedPostcode = params.postcode;

      // Fetch PC4 feature for demographics
      const pc4Res = await fetch(
        `${OGC_BASE}/collections/boundaries-pc4/items?f=json&limit=1&postcode=${params.postcode}`,
      );
      if (pc4Res.ok) {
        const pc4Data: OgcFeatureCollection = await pc4Res.json();
        pc4Props = (pc4Data.features?.[0]?.properties as Record<string, unknown>) ?? null;
      }
    } else if (params.municipality) {
      // Mode: gemeente lookup
      odinInputs = { municipality: params.municipality, include_cross_tabs: 'true' };
      profileInputs = { municipality: params.municipality };
      resolvedGemeente = params.municipality;
    } else if (params.province) {
      // Mode: provincie lookup
      odinInputs = { province: params.province, include_cross_tabs: 'true' };
      profileInputs = { province: params.province };
      resolvedProvince = params.province;
    } else if (params.lat != null && params.lng != null) {
      // Mode: point click -- reverse-geocode to PC4
      const delta = 0.001;
      const bbox = `${params.lng - delta},${params.lat - delta},${params.lng + delta},${params.lat + delta}`;

      const pc4Res = await fetch(
        `${OGC_BASE}/collections/boundaries-pc4/items?f=json&limit=1&bbox=${bbox}`,
      );

      if (!pc4Res.ok) {
        return { error: 'Failed to reverse-geocode location' };
      }

      const pc4Data: OgcFeatureCollection = await pc4Res.json();
      if (!pc4Data.features || pc4Data.features.length === 0) {
        return { error: 'No postal code found at this location. Try clicking within the Netherlands.' };
      }

      resolvedPostcode = String(pc4Data.features[0].properties?.postcode ?? '');
      if (!resolvedPostcode) {
        return { error: 'Invalid postcode at location' };
      }

      pc4Props = (pc4Data.features[0].properties as Record<string, unknown>) ?? null;
      odinInputs = { postcode: resolvedPostcode, include_cross_tabs: 'true' };
      profileInputs = { postcode: resolvedPostcode };

      // Also get gemeente name
      const gemRes = await fetch(
        `${OGC_BASE}/collections/boundaries-gemeente/items?f=json&limit=1&bbox=${bbox}`,
      );
      if (gemRes.ok) {
        const gemData: OgcFeatureCollection = await gemRes.json();
        resolvedGemeente = (gemData.features?.[0]?.properties?.statnaam as string) ?? null;
      }
    } else {
      return { error: 'Missing parameters. Provide lat/lng, postcode, municipality, or province.' };
    }

    // Apply optional secondary filters to odin-query
    if (params.mode) odinInputs.transport_mode = params.mode;
    if (params.purpose) odinInputs.trip_purpose = params.purpose;
    if (params.distance) odinInputs.distance_category = params.distance;
    if (params.year_min != null) {
      odinInputs.year_min = String(params.year_min);
      profileInputs.year_min = String(params.year_min);
    }
    if (params.year_max != null) {
      odinInputs.year_max = String(params.year_max);
      profileInputs.year_max = String(params.year_max);
    }

    // Call odin-query + odin-profile in parallel
    const [odinResult, profileResult] = await Promise.all([
      executeProcess('odin-query', odinInputs),
      executeProcess('odin-profile', profileInputs),
    ]);

    const odinData = 'data' in odinResult ? odinResult.data : null;
    const profileData = 'data' in profileResult ? profileResult.data : null;

    // Collect per-process errors
    const processErrors: Record<string, string> = {};
    if ('error' in odinResult) processErrors['odin-query'] = odinResult.error;
    if ('error' in profileResult) processErrors['odin-profile'] = profileResult.error;

    // If both processes failed, return error
    if (!odinData && !profileData) {
      return {
        error: 'Both ODIN processes failed for this area',
      };
    }

    return {
      data: {
        postcode: resolvedPostcode,
        gemeente: resolvedGemeente,
        province: resolvedProvince,
        lat: params.lat ?? null,
        lng: params.lng ?? null,
        odin: odinData,
        profile: profileData,
        pc4_properties: pc4Props,
        process_errors: Object.keys(processErrors).length > 0 ? processErrors : undefined,
      },
    };
  } catch (err) {
    return { error: `Unexpected error: ${err instanceof Error ? err.message : 'Unknown'}` };
  }
}

/**
 * Fetch modal-split-analysis chart. Replaces BFF `/api/odin/chart`.
 * Returns { image: base64 PNG, total_trips: number }
 */
export async function fetchOdinChart(params: {
  postcode?: string;
  municipality?: string;
  province?: string;
  year_min?: number;
  year_max?: number;
}): Promise<{ image: string | null; total_trips: number | null } | { error: string }> {
  const inputs: Record<string, string> = {};
  if (params.postcode) inputs.postcode = params.postcode;
  else if (params.municipality) inputs.municipality = params.municipality;
  else if (params.province) inputs.province = params.province;
  else return { error: 'Provide postcode, municipality, or province' };
  if (params.year_min != null) inputs.year_min = String(params.year_min);
  if (params.year_max != null) inputs.year_max = String(params.year_max);

  const result = await executeProcess<{ image?: string; total_trips?: number }>(
    'modal-split-analysis',
    inputs,
  );

  if ('error' in result) return { error: result.error };

  return {
    image: result.data.image ?? null,
    total_trips: result.data.total_trips ?? null,
  };
}

/**
 * Fetch BOP accessibility map via OGC Process.
 * Returns base64-encoded PNG + table name.
 */
export async function fetchBopMap(params: {
  mode: string;
  amenity: string;
  map_type: string;
  region_type?: string;
  region_id?: string;
}): Promise<
  { data: { image: string; table_name: string; metadata?: Record<string, unknown> } } | { error: string }
> {
  const inputs: Record<string, string> = {
    mode: params.mode,
    amenity: params.amenity,
    map_type: params.map_type,
  };
  if (params.region_type) inputs.region_type = params.region_type;
  if (params.region_id) inputs.region_id = params.region_id;

  return executeProcess<{ image: string; table_name: string; metadata?: Record<string, unknown> }>(
    'accessibility-map',
    inputs,
  );
}

/**
 * Fetch BOP facility locations as GeoJSON from OGC API Features.
 * Filters by TYPE (e.g., 'BO', 'HA', 'SUPERMARKT') and optional bbox.
 * Optional pc4 prefix for exact PC4 filtering (client-side post-filter).
 */
export async function fetchBopFacilities(opts: {
  type?: string;
  limit?: number;
  bbox?: [number, number, number, number];
  pc4?: string;
}): Promise<GeoJSON.FeatureCollection> {
  const { type, limit = 500, bbox, pc4 } = opts;
  const params = new URLSearchParams({ f: 'json', limit: String(limit) });
  if (type) params.set('TYPE', type);
  if (bbox) params.set('bbox', bbox.join(','));

  const res = await fetch(`${OGC_BASE}/collections/bop-facilities/items?${params}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch facilities: ${res.status}`);
  }
  const fc: GeoJSON.FeatureCollection = await res.json();

  // Client-side post-filter for PC4 precision
  if (pc4) {
    fc.features = fc.features.filter((f) => {
      const postcode = (f.properties?.POSTCODE as string) ?? '';
      return postcode.startsWith(pc4);
    });
  }
  return fc;
}

/**
 * Fetch ODIN spider diagram (OD desire lines) for a gemeente.
 * Returns GeoJSON FeatureCollection with top-N connections.
 */
export async function fetchOdinSpider(params: {
  gemeente: string;
  mode?: string;
  motive?: string;
  top_n?: number;
  include_internal?: boolean;
}): Promise<ProcessResult<SpiderResponse>> {
  const inputs: Record<string, string> = { gemeente: params.gemeente };
  if (params.mode) inputs.mode = params.mode;
  if (params.motive) inputs.motive = params.motive;
  if (params.top_n) inputs.top_n = String(params.top_n);
  if (params.include_internal) inputs.include_internal = 'true';

  return executeProcess<SpiderResponse>('odin-spider', inputs);
}

/**
 * Fetch respondent demographics for spider OD pairs.
 * Returns sex, age, education, income distributions + home gemeente locations.
 */
export async function fetchOdinSpiderProfile(params: {
  gemeente: string;
  mode?: string;
  motive?: string;
}): Promise<ProcessResult<SpiderProfileResponse>> {
  const inputs: Record<string, string> = { gemeente: params.gemeente };
  if (params.mode) inputs.mode = params.mode;
  if (params.motive) inputs.motive = params.motive;

  return executeProcess<SpiderProfileResponse>('odin-spider-profile', inputs);
}

/**
 * Fetch ODIN compare data. Replaces BFF `/api/odin/compare`.
 */
export async function fetchOdinCompare(params: {
  a_postcode?: string;
  a_municipality?: string;
  a_province?: string;
  b_postcode?: string;
  b_municipality?: string;
  b_province?: string;
  year_min?: number;
  year_max?: number;
}): Promise<{ data: Record<string, unknown> } | { error: string }> {
  const inputs: Record<string, string> = {};
  if (params.year_min != null) inputs.year_min = String(params.year_min);
  if (params.year_max != null) inputs.year_max = String(params.year_max);

  // Location A
  if (params.a_postcode) inputs.location_a_postcode = params.a_postcode;
  else if (params.a_municipality) inputs.location_a_municipality = params.a_municipality;
  else if (params.a_province) inputs.location_a_province = params.a_province;
  else return { error: 'Missing location A' };

  // Location B
  if (params.b_postcode) inputs.location_b_postcode = params.b_postcode;
  else if (params.b_municipality) inputs.location_b_municipality = params.b_municipality;
  else if (params.b_province) inputs.location_b_province = params.b_province;
  else return { error: 'Missing location B' };

  const result = await executeProcess<Record<string, unknown>>('odin-compare', inputs);

  if ('error' in result) return { error: result.error };
  return { data: result.data };
}
